import { format, isValid, parseISO } from 'date-fns'
import { CalendarFilterCategoryIds, calendarStatusOptionId } from './categories'
import { type CalendarDateRangeFilter, type CalendarFilterState } from './types'

/** What one value of the legacy filter bar means to the month view. */
export type CalendarFilterValueRef = Readonly<{
  categoryId: string
  valueId: string
}>

/**
 * The month's own value for each value the legacy filter bar offers, by the id
 * that bar keys it with.
 *
 * The two id spaces meet nowhere else. The shared bar keys a channel by its
 * numeric primary key (`service-3`) and an account by its own (`account-12`),
 * while every calendar item names the same things by uuid, so a value can only
 * cross from one view to the other through a lookup like this.
 */
export type CalendarLegacyFilterValues = ReadonlyMap<
  string,
  CalendarFilterValueRef
>

/**
 * The week's approval statuses, in the words the month's status list uses.
 *
 * These need no lookup: both vocabularies are written in code. The tray asks
 * them all of content, so they cross over into the content half — except the
 * tray's "Moment Placeholder", which is the month's Idea and only a moment can
 * be one. "Published" has no word here, so a week filtered by it seeds nothing
 * for status.
 */
const STATUS_BY_LEGACY_ID: Readonly<Record<string, string>> = {
  'status-approved': calendarStatusOptionId('content', 'approved'),
  'status-denied': calendarStatusOptionId('content', 'needs_revision'),
  'status-draft': calendarStatusOptionId('content', 'draft'),
  'status-idea': calendarStatusOptionId('moment', 'idea'),
  'status-pending': calendarStatusOptionId('content', 'for_approval'),
}

/**
 * The two ends of the tray's own live window, and the end each one sets here.
 *
 * The tray keeps a date for both ends whether or not either is switched on, so
 * an end only crosses over when its row is one of the chosen values.
 */
const LIVE_END_BY_LEGACY_ID: Readonly<
  Record<string, keyof CalendarDateRangeFilter>
> = {
  duration_ends_at: 'to',
  duration_starts_at: 'from',
}

/**
 * The day an instant from the tray falls on, in the viewer's own zone.
 *
 * The tray writes the day a person picked as an instant in UTC, which is a day
 * later or earlier than the one they picked either side of the meridian. So
 * this one date is read back through the viewer's zone, unlike an item's own
 * dates, which the server has already decided the day of and which the grid
 * takes verbatim.
 */
const dayOf = (instant: string | undefined): string | undefined => {
  if (instant === undefined) return undefined

  const parsed = parseISO(instant)
  return isValid(parsed) ? format(parsed, 'yyyy-MM-dd') : undefined
}

/**
 * The week view's filters, read as the month's own.
 *
 * Only the values that resolve are carried over: a channel, an account or a
 * label the lookup cannot name, and any category the month does not offer —
 * stories — are left behind rather than guessed at. Values land in the include
 * list, because the shared filter bar has no way to say exclude.
 *
 * The tray's own live window crosses over into this one, which is why `dates`
 * comes along: the chosen values name the ends that are switched on, and this
 * holds the day each of them is set to.
 */
export const calendarFiltersFromWeekView = (
  legacyIds: readonly string[],
  values: CalendarLegacyFilterValues,
  dates: Readonly<Record<string, string>> = {}
): CalendarFilterState => {
  const cats: Record<string, { exclude: string[]; include: string[] }> = {}
  const live: { from?: string; to?: string } = {}

  legacyIds.forEach((legacyId) => {
    const end = LIVE_END_BY_LEGACY_ID[legacyId]
    if (end !== undefined) {
      const day = dayOf(dates[legacyId])
      if (day !== undefined) live[end] = day
      return
    }

    const status = STATUS_BY_LEGACY_ID[legacyId]
    const found =
      status === undefined
        ? values.get(legacyId)
        : { categoryId: CalendarFilterCategoryIds.status, valueId: status }
    if (found === undefined) return

    const category = (cats[found.categoryId] ??= { exclude: [], include: [] })
    if (!category.include.includes(found.valueId))
      category.include.push(found.valueId)
  })

  return live.from === undefined && live.to === undefined
    ? { cats }
    : { cats, live }
}
